import { DUPLICATE_CORRELATION, correlation, frameSharpness } from './select';

/*
 * Taking a Z-stack (docs/FIELD_AI_VISION_SPEC.md 10.3, phase A5), by one of
 * two paths. No DOM and no timers of its own: the camera's moves, its
 * samples, the frame grab, the clock and the waiting are handed in, so the
 * tests drive it step by step.
 *
 *   Path A, focus stepping (where the camera offers a manual focus and a
 *   distance): stepDistances() plans LAYERS.default (3 to 25) distances over
 *   the whole range, or around the current distance, evenly in dioptres (the
 *   way depth of field falls); runStepping() moves the focus to each, waits
 *   for the lens to settle (two samples agreeing within SETTLE.agree, at most
 *   SETTLE.maxMs), grabs the frame and goes on.
 *
 *   Path B, a hand sweep (an iPhone, a microscope's fine-focus knob, a phone
 *   moved slowly toward the subject): runSweep() samples the picture every
 *   SWEEP.intervalMs and grabs a frame whenever a sample is sharper than the
 *   one before or the sharpest yet in some tile -- at most SWEEP.ring frames
 *   kept, the weakest let go -- until Stop or SWEEP.maxMs.
 *
 * Either way the frames are {..., tiles, focus_distance?, t_ms}, for
 * select.js to choose from. A cancel (signal.aborted) ends it with
 * ZStackCancelled; a focus the camera refuses with ZStackRefused.
 *
 * Phones that offer a focus distance do not always move to it: Chrome on
 * some Android phones rejects the manual focus outright (`reason`
 * 'refused'), others take it and leave the lens where it was ('ignored':
 * the distance the camera reports does not move and the first frames show
 * the same picture). Either way ZStackRefused carries the frames taken so
 * far, and the hook goes on by hand (useZStack). isFlat() tells a stepped
 * stack whose lens never moved from one with a real depth.
 */
export const LAYERS = Object.freeze({ min: 3, max: 25, default: 9 });
// A quick stack (docs/LIVE_UX_PLAN.md 3.4): this many layers, close to the focus now.
export const QUICK_LAYERS = Object.freeze([3, 4, 5]);
export const SETTLE = Object.freeze({ agree: 0.02, maxMs: 400, sampleMs: 50 });
export const SWEEP = Object.freeze({ intervalMs: 100, maxMs: 30000, ring: 40 });
//: how much of the range "around the current distance" covers, in dioptres
export const AROUND = 0.25;
//: frames whose sharpness is within this of the sharpest show one focus plane
export const FLAT_SPREAD = 0.05;

export class ZStackCancelled extends Error {
  constructor() { super('zstack-cancelled'); this.name = 'ZStackCancelled'; }
}
export class ZStackRefused extends Error {
  /** `reason` 'refused' (the camera said no) or 'ignored' (it said yes and did not move); `frames` taken so far. */
  constructor(reason = 'refused', frames = []) {
    super('zstack-refused');
    this.name = 'ZStackRefused';
    this.reason = reason;
    this.frames = frames;
  }
}

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
const clampLayers = n => Math.max(LAYERS.min, Math.min(LAYERS.max, Math.round(Number(n) || LAYERS.default)));
//: to the micrometre: a camera takes no finer distance, and the plan stays readable
const round = v => Math.round(v * 1e6) / 1e6;

/**
 * `n` focus distances from near to far over {min, max} (metres), evenly in
 * dioptres; with `around`, over AROUND of the range centred on that distance.
 * Evenly in metres where the range starts at 0 (no dioptre for infinity).
 * Never outside {min, max}: Chrome refuses a distance a hair past the end.
 */
export function stepDistances({ min, max }, n, { around = null } = {}) {
  const count = clampLayers(n);
  if (!(max > min)) return [min];
  const inside = v => Math.min(max, Math.max(min, round(v)));
  if (!(min > 0)) {
    let lo = min;
    let hi = max;
    if (Number.isFinite(around)) {
      const half = ((max - min) * AROUND) / 2;
      lo = Math.max(min, around - half);
      hi = Math.min(max, around + half);
    }
    return Array.from({ length: count }, (_, i) => inside(lo + ((hi - lo) * i) / (count - 1)));
  }
  let near = 1 / min;
  let far = 1 / max;
  if (Number.isFinite(around) && around > 0) {
    const half = ((near - far) * AROUND) / 2;
    const centre = Math.min(near, Math.max(far, 1 / around));
    near = Math.min(1 / min, centre + half);
    far = Math.max(1 / max, centre - half);
  }
  return Array.from({ length: count }, (_, i) => inside(1 / (near - ((near - far) * i) / (count - 1))));
}

/**
 * True when `frames` ({tiles}) show one focus plane: each within FLAT_SPREAD
 * of the sharpest, with the same tile profile. A lens that never moved; a
 * stack of nothing at all (no sharpness) is not flat, it is blurred.
 */
export function isFlat(frames) {
  if (!frames || frames.length < 2) return false;
  const scores = frames.map(f => frameSharpness(f.tiles));
  const best = Math.max(...scores);
  if (!(best > 0)) return false;
  const sharpest = frames[scores.indexOf(best)].tiles;
  const even = t => t.every(v => v === t[0]);
  return frames.every((f, i) => best - scores[i] <= FLAT_SPREAD * best
    && ((even(f.tiles || []) && even(sharpest)) || correlation(f.tiles || [], sharpest) > DUPLICATE_CORRELATION));
}

/** Waits until two samples of the sharpness agree within `agree`, or `maxMs` has gone. True when they did. */
export async function settle({ sample, sleep = wait, now = Date.now, agree = SETTLE.agree, maxMs = SETTLE.maxMs,
  sampleMs = SETTLE.sampleMs } = {}) {
  const start = now();
  let previous = null;
  while (now() - start < maxMs) {
    await sleep(sampleMs);
    const value = sample();
    if (Number.isFinite(value)) {
      if (previous !== null && Math.abs(value - previous) <= agree * Math.max(value, previous, 1e-9)) return true;
      previous = value;
    }
  }
  return false;
}

/**
 * True when the camera took the distances asked for and did nothing: what it
 * reports (`reads`) stayed put while the asked-for distances moved, and the
 * frames show one picture. Unknown reads (a camera that reports none) say
 * nothing.
 */
export function focusIgnored(asked, reads, frames) {
  if (asked.length < 2 || reads.length !== asked.length || !reads.every(Number.isFinite)) return false;
  const span = Math.max(...asked) - Math.min(...asked);
  if (!(span > 0)) return false;
  const still = Math.max(...reads) - Math.min(...reads) <= 0.1 * span;
  const missed = asked.some((d, i) => Math.abs(d - reads[i]) > 0.5 * span / (asked.length - 1));
  return still && missed && isFlat(frames);
}

/**
 * Path A: for each distance, `applyFocus(d)` (true once the camera took it),
 * settle, then `grab()` -> {tiles, ...}. `onLayer(done, total)` after each.
 * With `readFocus()` (the distance the camera reports now, or null), a lens
 * that has not moved by the LAYERS.min-th layer ends it as 'ignored'.
 */
export async function runStepping({ distances, applyFocus, sample, grab, sleep = wait, now = Date.now, signal = null,
  onLayer = null, settleOptions = {}, readFocus = null }) {
  const frames = [];
  const reads = [];
  const start = now();
  for (const distance of distances) {
    if (signal?.aborted) throw new ZStackCancelled();
    if (!(await applyFocus(distance))) throw new ZStackRefused('refused', frames);
    await settle({ sample, sleep, now, ...settleOptions });
    if (signal?.aborted) throw new ZStackCancelled();
    const frame = await grab();
    frames.push({ ...frame, focus_distance: distance, t_ms: Math.round(now() - start) });
    if (readFocus) {
      reads.push(readFocus());
      if (frames.length === LAYERS.min && focusIgnored(distances.slice(0, frames.length), reads, frames)) {
        throw new ZStackRefused('ignored', frames);
      }
    }
    onLayer?.(frames.length, distances.length);
  }
  return frames;
}

/**
 * Path B: every `intervalMs`, `sample()` -> tile sharpness (an array) or null;
 * a sample sharper than the last, or the sharpest yet in some tile, is
 * grabbed (`grab()` -> {tiles, ...}). Ends when `stopped()` says so or after
 * `maxMs`. At most `ring` frames are kept: the weakest goes. `onTick({ms,
 * frames})` after each sample.
 */
export async function runSweep({ sample, grab, stopped = () => false, sleep = wait, now = Date.now, signal = null,
  intervalMs = SWEEP.intervalMs, maxMs = SWEEP.maxMs, ring = SWEEP.ring, onTick = null }) {
  const frames = [];
  const start = now();
  let bestTiles = null;
  let previous = null;
  let best = 0;
  while (!stopped() && now() - start < maxMs) {
    await sleep(intervalMs);
    if (signal?.aborted) throw new ZStackCancelled();
    if (stopped()) break;
    const tiles = sample();
    if (tiles && tiles.length) {
      const sharp = frameSharpness(tiles);
      const newInTile = !bestTiles || tiles.some((v, i) => v > (bestTiles[i] || 0));
      const rising = previous !== null && sharp > previous;
      bestTiles = bestTiles ? bestTiles.map((v, i) => Math.max(v, tiles[i] || 0)) : [...tiles];
      previous = sharp;
      best = Math.max(best, sharp);
      if ((newInTile || rising) && sharp > 0) {
        const frame = await grab();
        if (signal?.aborted) throw new ZStackCancelled();
        frames.push({ ...frame, t_ms: Math.round(now() - start) });
        if (frames.length > ring) {
          let weakest = 0;
          frames.forEach((f, i) => { if (frameSharpness(f.tiles) < frameSharpness(frames[weakest].tiles)) weakest = i; });
          frames.splice(weakest, 1);
        }
      }
    }
    onTick?.({ ms: Math.round(now() - start), frames: frames.length, best });
  }
  return frames;
}
